import React, { useState, useEffect } from 'react';
import {
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';


const AddressElement = ({ options, countries }) => {
  return (
    <div className="space-y-4">
      <input
        type="text"
        required
        placeholder="Address Line 1"
        className="w-full bg-[#332f30] text-white placeholder-gray-400 border border-gray-600/30 rounded-lg p-3 outline-none focus:border-[#c5ff55] transition-colors"
      />
      <div className="grid grid-cols-3 gap-4">
        <input
          type="text"
          required
          placeholder="City"
          className="bg-[#332f30] text-white placeholder-gray-400 border border-gray-600/30 rounded-lg p-3 outline-none focus:border-[#c5ff55] transition-colors"
        />
        <input
          type="text"
          required
          placeholder="State/Province"
          className="bg-[#332f30] text-white placeholder-gray-400 border border-gray-600/30 rounded-lg p-3 outline-none focus:border-[#c5ff55] transition-colors"
        />
        <input
          type="text"
          required
          placeholder="ZIP/Postal Code"
          className="bg-[#332f30] text-white placeholder-gray-400 border border-gray-600/30 rounded-lg p-3 outline-none focus:border-[#c5ff55] transition-colors"
        />
      </div>
      <select className="w-full bg-[#332f30] text-white border border-gray-600/30 rounded-lg p-3 outline-none focus:border-[#c5ff55] transition-colors">
        <option value="">Select Country</option>
        {countries.map((country) => (
          <option key={country.code} value={country.code}>
            {country.name}
          </option>
        ))}
      </select>
    </div>
  );
};

const StandardButton = ({ type, text, width, variant, loading, disabled, onClick }) => {
  const baseClasses = "px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2";
  const variantClasses = variant === 'filled' 
    ? "bg-[#c5ff55] hover:bg-[#b5ef45] text-black" 
    : "border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white";
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variantClasses} ${loading ? 'opacity-75 cursor-not-allowed' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      style={{ width }}
    >
      {loading ? (
        <>
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent"></div>
          Processing...
        </>
      ) : (
        <>
          {text}
        </>
      )}
    </button>
  );
};

const TestModeIndicator = () => (
  <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-3 mb-6 hidden">
    <div className="flex items-center gap-2 text-yellow-400">
      <span className="font-semibold">Demo Mode</span>
    </div>
    <p className="text-yellow-300 text-sm mt-1">
      This is a demo checkout form. Use test card: 4242 4242 4242 4242
    </p>
  </div>
);

const PaymentForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    vatNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);

  const navigate = useNavigate();

  const stripe = useStripe();
  const elements = useElements();

  const [validQuery, setValidQuery] = useState(true);

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/values/countries`);
        const formatted = (response.data?.data || []).map(c => ({
          code: c.iso,
          name: c.name
        }));
        setCountries(formatted);
        
      } catch (err) {
        console.error('Failed to fetch countries:', err);
        setError('Failed to load countries. Please refresh the page.');
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const planId = query.get('plan_id');
    const subId = query.get('sub_id');

    if (!planId && !subId) {
      setValidQuery(false);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) return 'First name is required';
    if (!formData.lastName.trim()) return 'Last name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Please enter a valid email address';
    if (!formData.vatNumber.trim()) return 'VAT number is required';

    // Address fields via DOM
    const address = document.querySelector('input[placeholder="Address Line 1"]')?.value.trim();
    const city = document.querySelector('input[placeholder="City"]')?.value.trim();
    const state = document.querySelector('input[placeholder="State/Province"]')?.value.trim();
    const zip = document.querySelector('input[placeholder="ZIP/Postal Code"]')?.value.trim();
    const country = document.querySelector('select')?.value;

    if (!address) return 'Address Line 1 is required';
    if (!city) return 'City is required';
    if (!state) return 'State/Province is required';
    if (!zip) return 'ZIP/Postal Code is required';
    if (!country) return 'Country is required';

    return null;
  };


  const handleSubmit = async (e) => {
  e.preventDefault();
  setError(null);
  setLoading(true);

  const validationError = validateForm();
  if (validationError) {
    setError(validationError);
    setLoading(false);
    return;
  }

  const query = new URLSearchParams(window.location.search);
  const planId = query.get('plan_id');
  const subId = query.get('sub_id');

  

  try {
    // 1. Call /payments/stripe_payment with proper payload
    const payload = {
      email: formData.email,
      name: `${formData.firstName} ${formData.lastName}`,
      vat_number: formData.vatNumber
    };

    if (planId) {
      payload.plan_id = parseInt(planId);
    } else if (subId) {
      payload.subscription_id = parseInt(subId);
    }
    

    // 2. Conferma il pagamento con il clientSecret ricevuto
    const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/payments/stripe_payment`, payload);

    const { clientSecret } = data;

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
        billing_details: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email
        }
      }
    });

    if (result.error) {
      setError(result.error.message);
      setLoading(false);
      return;
    }

    if (result.paymentIntent.status === 'succeeded') {
      setSuccess(true);
    }

    setSuccess(true);
  } catch (err) {
    console.error('Payment error:', err);
    setError(err.message || 'An unexpected error occurred. Please try again.');
  } finally {
    setLoading(false);
  }
};


  if (success) {
    return (
      <div className="min-h-screen p-0 p-md-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-green-900/30 border border-green-600/50 rounded-full p-4 mb-6 inline-block">
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Payment Successful!</h2>
          <p className="text-gray-300 mb-6">Thank you for your purchase. You'll receive a confirmation email shortly.</p>
          <button 
            onClick={() => {
              setSuccess(false);
              setFormData({ firstName: '', lastName: '', email: '', vatNumber: '' });
              navigate('/');
            }}
            className="bg-[#c5ff55] hover:bg-[#b5ef45] text-black px-6 py-3 rounded-lg transition-colors"
          >
            Start Exploring
          </button>
        </div>
      </div>
    );
  }

  if (!validQuery) {
    return (
      <div className="min-h-screen p-0 p-md-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-900/30 border border-red-600/50 rounded-full p-4 mb-6 inline-block" />
          <h2 className="text-3xl font-bold text-white mb-4">Payment Unavailable</h2>
          <p className="text-gray-300 mb-6">This checkout link is not valid. Please contact support if you believe this is an error.</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-[#c5ff55] hover:bg-[#b5ef45] text-black px-6 py-3 rounded-lg transition-colors"
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-0 p-md-6">
      <div className="w-full mx-auto">
        <div className="text-center mb-4 mt-4 mt-md-0 mb-md-8">
          <h1 className="text-3xl font-bold text-white mb-2">Complete Your Purchase</h1>
        </div>

        <TestModeIndicator />

        <div className="border-[#656565] bg-[#272727] border border-gray-700/50 rounded-2xl p-5 p-md-8 shadow-2xl">
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                Customer Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    First Name*
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-800/50 border border-gray-600/50 text-white p-3 rounded-lg focus:border-[#c5ff55] transition-all outline-none"
                    placeholder="Enter your first name"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    Last Name*
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-800/50 border border-gray-600/50 text-white p-3 rounded-lg focus:border-[#c5ff55] transition-all outline-none"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800/50 border border-gray-600/50 text-white p-3 rounded-lg focus:border-[#c5ff55] transition-all outline-none"
                  placeholder="your@email.com"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                  VAT Number *
                </label>
                <input
                  type="text"
                  name="vatNumber"
                  value={formData.vatNumber}
                  onChange={handleChange}
                  required
                  placeholder="e.g., IT12345678901"
                  className="w-full bg-gray-800/50 border border-gray-600/50 text-white p-3 rounded-lg focus:border-[#c5ff55] transition-all outline-none"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                Billing Address
              </h3>
              <div className="bg-gray-800/30 border border-gray-600/30 rounded-lg p-4">
                {loadingCountries ? (
                  <div className="text-gray-400 text-center py-4">Loading countries...</div>
                ) : (
                  <AddressElement
                    countries={countries}
                    options={{
                      mode: 'billing',
                      fields: { phone: 'never' }
                    }}
                  />
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                Payment Information
              </h3>
              <CardElement
                onChange={({ complete }) => setCardComplete(complete)}
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#fff'
                    }
                  }
                }}
              />
            </div>

            <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-300">
                <span className="text-sm font-medium">Your payment is secured with 256-bit SSL encryption</span>
              </div>
            </div>

            {error && (
              <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-400">
                  <span className="font-medium">{error}</span>
                </div>
              </div>
            )}

            <div className="pt-4">
              <StandardButton
                type="submit"
                text="Complete Payment"
                width="100%"
                variant="filled"
                loading={loading}
                disabled={loading || loadingCountries}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;